import {
  createContext, useContext, useState, useEffect, useCallback, useMemo,
} from 'react';
import { auth as authApi } from '../api/endpoints';
import { setAuthToken, getAuthToken, onSessionEnded } from '../api/client';

/**
 * Session state for the console.
 *
 * The two things worth knowing:
 *
 * 1. `bootstrapping` exists so protected routes do not flash the login screen
 *    on a hard refresh. A stored token has to be validated against the API —
 *    which re-reads the role from the database — before we can claim to be
 *    signed in, and that is a round-trip.
 *
 * 2. Login is a two-step flow because 2FA is mandatory. `pending` holds the
 *    half-authenticated state between the password and the code, and it lives
 *    in memory only: refreshing mid-login correctly starts over.
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [security, setSecurity] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(Boolean(getAuthToken()));
  const [pending, setPending] = useState(null);
  const [sessionMessage, setSessionMessage] = useState(null);

  // Validate a stored token on load. The API re-reads the role from the
  // database, so a token belonging to someone since demoted is rejected here
  // rather than on their first click.
  useEffect(() => {
    if (!getAuthToken()) {
      setBootstrapping(false);
      return;
    }
    let cancelled = false;
    authApi.me()
      .then(({ data }) => {
        if (cancelled) return;
        setUser(data.user);
        setSecurity(data.security);
      })
      .catch(() => {
        if (!cancelled) setAuthToken(null);
      })
      .finally(() => {
        if (!cancelled) setBootstrapping(false);
      });
    return () => { cancelled = true; };
  }, []);

  // A 401 anywhere in the app ends the session. Subscribing here means every
  // screen reacts to expiry without each one having to handle it.
  useEffect(() => onSessionEnded((message) => {
    setUser(null);
    setSecurity(null);
    setSessionMessage(message || 'Your session has ended. Please sign in again.');
  }), []);

  const applySession = useCallback((payload) => {
    setAuthToken(payload.token);
    setUser(payload.user);
    setPending(null);
    setSessionMessage(null);
    return payload;
  }, []);

  /**
   * Step 1. Resolves to a descriptor telling the UI which screen comes next:
   *   { stage: 'done' }         — 2FA disabled platform-wide, session issued
   *   { stage: 'verify' }       — enrolled; ask for the 6-digit code
   *   { stage: 'enroll', ... }  — not enrolled; show the QR and enrol now
   */
  const login = useCallback(async (email, password) => {
    const { data } = await authApi.login(email, password);

    if (data.token) {
      applySession(data);
      return { stage: 'done' };
    }

    if (data.mfaRequired) {
      setPending({ mfaToken: data.mfaToken, stage: 'verify', email });
      return { stage: 'verify' };
    }

    // First login: enrolment is part of signing in, so the operator is never
    // handed a session they cannot actually use.
    setPending({
      mfaToken: data.mfaToken,
      stage: 'enroll',
      email,
      secret: data.secret,
      otpauthUrl: data.otpauthUrl,
      qrDataUrl: data.qrDataUrl,
    });
    return { stage: 'enroll' };
  }, [applySession]);

  /** Step 2a — an enrolled operator submits a TOTP or recovery code. */
  const verifyMfa = useCallback(async (code) => {
    if (!pending?.mfaToken) throw new Error('Sign in again — the verification window has closed.');
    const { data } = await authApi.verifyMfa(pending.mfaToken, code);
    applySession(data);
    return data;
  }, [pending, applySession]);

  /** Step 2b — first-time enrolment. Returns the one-time recovery codes. */
  const enrollMfa = useCallback(async (code) => {
    if (!pending?.mfaToken) throw new Error('Sign in again — the enrolment window has closed.');
    const { data } = await authApi.enrollMfa(pending.mfaToken, code);
    applySession(data);
    return data.recoveryCodes || [];
  }, [pending, applySession]);

  const cancelPending = useCallback(() => setPending(null), []);

  const logout = useCallback(async () => {
    try {
      // Best-effort: this bumps tokenVersion server-side, invalidating every
      // session this operator holds, not just this tab.
      await authApi.logout();
    } catch {
      // A failed logout must still clear the local session.
    }
    setAuthToken(null);
    setUser(null);
    setSecurity(null);
    setSessionMessage(null);
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await authApi.me();
    setUser(data.user);
    setSecurity(data.security);
    return data;
  }, []);

  const value = useMemo(() => ({
    user,
    security,
    bootstrapping,
    pending,
    sessionMessage,
    isAuthenticated: Boolean(user),
    login,
    verifyMfa,
    enrollMfa,
    cancelPending,
    logout,
    refresh,
    clearSessionMessage: () => setSessionMessage(null),
  }), [user, security, bootstrapping, pending, sessionMessage,
    login, verifyMfa, enrollMfa, cancelPending, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
